import type { SpecificNotificationProps } from '../BaseNotification';
import { BaseNotification } from '../BaseNotification';
import type { ConsumedActionProps } from '../NotificationAction';
import { NotificationAction } from '../NotificationAction';
import { bannerInnerStyles, bannerStyles } from '../styles';

export type BannerProps<
  T extends boolean,
  L extends string | undefined,
> = SpecificNotificationProps<T, 'role'> & ConsumedActionProps<L>;

type BannerPassedProps<T extends boolean> = SpecificNotificationProps<
  T,
  'children' | 'role'
>;

/**
 * Vibrant messages used as page banners.
 *
 * {@link https://zeroheight.com/76c439891/p/9204f2-alert/b/89414b Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-banner--basic-banner Code documentation}
 *
 * @example
 * <Banner>My Banner</Banner>
 */
export const Banner = <T extends boolean, L extends string | undefined>({
  children,
  actionText,
  actionHref,
  onAction,
  actionProps,
  ...props
}: BannerProps<T, L>): JSX.Element => {
  return (
    <BaseNotification
      {...(props as BannerPassedProps<T>)}
      notificationType="banner"
      wrapContentWithLabel
      role="status"
      wrapperCss={bannerInnerStyles}
      css={bannerStyles(props.kind)}
    >
      {children}

      {actionText && (
        <NotificationAction
          {...actionProps}
          kind="inline"
          href={actionHref}
          onClick={onAction}
        >
          {actionText}
        </NotificationAction>
      )}
    </BaseNotification>
  );
};
